import type { LabelColor } from 'src/components/label';
import type { ITenant } from 'src/pages/tenants/tenants.types';
import type { ITicket } from 'src/pages/tickets/tickets.types';
import type { IUserItem } from 'src/pages/setting/setting.types';
import type { IAccount } from 'src/pages/accounts/accounts.types';
import type { IContact } from 'src/pages/contacts/contacts.types';
import type {
  TeamList,
  AccountList,
  TicketsList,
  ChannelData,
  CustomLabelsList,
} from 'src/types/user';

import { useState } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Link from '@mui/material/Link';
import {
  Box,
  Stack,
  Avatar,
  Tooltip,
  TableRow,
  Checkbox,
  MenuList,
  MenuItem,
  TableCell,
  IconButton,
  Typography,
  Button,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { htmlToText } from 'src/utils/commonUtils';
import { convertUTCToLocal } from 'src/utils/format-time';
import { formatPhoneNumber } from 'src/utils/format-number';

import { userType } from 'src/pages/chat/utils/get-nav-item';
import { truncateText } from 'src/pages/chat/utils/get-message';
import { formatPrice } from 'src/pages/setting/hook/formatPrice';
import { getContrastYIQ } from 'src/pages/chat/components/chat-label';
import { WEBHOOK_EVENTS } from 'src/pages/setting/components/webhook-form';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';

import { Image } from '../image';
import { CustomPopover } from '../custom-popover';

import type { TableHeadCellProps } from './table-head-custom';

// ----------------------------------------------------------------------
type TabItem = {
  value: string;
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
  count: number;
};
const baseTabs: Omit<TabItem, 'count'>[] = [
  { value: 'all', label: 'ALL', color: 'default' },
  { value: 'sent', label: 'SENT', color: 'info' },
  { value: 'delivered', label: 'DELIVERED', color: 'warning' },
  { value: 'read', label: 'SEEN', color: 'success' },
  { value: 'failed', label: 'FAILED', color: 'error' },
];

type Props = {
  rows:
    | AccountList[]
    | TicketsList[]
    | IUserItem[]
    | TeamList[]
    | CustomLabelsList[]
    | IAccount[]
    | IContact[]
    | ITenant[];
  table: any;
  editHref: (id: string) => string;
  contactDetailHref?: (id: string) => string;
  accountDetailHref?: (id: string) => string;
  userDetailHref?: (id: string) => string;
  teamsDetailHref?: (id: string) => string;
  onDeleteRow: (id: string) => void;
  headCells: TableHeadCellProps[];
  selectRowPremission?: boolean;
  handleEditContact?: (id: string) => string | Promise<void>;
  handleDeleteRecord?: (id: string) => void;
  handleIVRConnection?: (id: string) => void;
  handleCustomiseDID?: (id: string) => void;
  isSelectRecordDialog?: boolean;
  setRecordInputHandler?: (row: any) => void;
  handleResendMail?: (id: string) => string;
  handlePreview?: (id: string) => string;
  playCallRecording?: (id: string) => void;
  t?: any;
  isCampaignTamplate?: boolean;
  isEdit?: boolean;
  isDelete?: boolean;
  currentlyPlayingId?: string | null;
  selectAllFilter?: any;
  moduleName?: string;
  handleLoginAsUser?: (row: any) => void;
  handleOpenConversation?: (row: any) => void;
  userRole?: string;
  isSupportReport?: boolean;
  handlePhoneNumberClick?: (number: any) => void;
  isTenantInvoice?: boolean;
  handleMarkAsRead?: (id: string) => void;
  tenantUserDetailsHref?: (tenantId: string, userId: string) => string;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  currentTenantId?: string;
  isSuperAdmin?: boolean;
  handlePayNowButton?: (id: any) => void;
};

export function TableRowCustom({
  rows,
  table,
  editHref,
  onDeleteRow,
  headCells,
  selectRowPremission,
  handleEditContact,
  handleDeleteRecord,
  handleCustomiseDID,
  handleIVRConnection,
  handlePreview,
  isSelectRecordDialog = false,
  setRecordInputHandler,
  handleResendMail,
  playCallRecording,
  t,
  isCampaignTamplate = false,
  isEdit = false,
  isDelete = false,
  currentlyPlayingId = null,
  selectAllFilter = false,
  moduleName,
  contactDetailHref,
  accountDetailHref,
  userDetailHref,
  teamsDetailHref,
  handleLoginAsUser,
  handleOpenConversation,
  userRole,
  isSupportReport,
  handlePhoneNumberClick,
  isTenantInvoice = false,
  handleMarkAsRead,
  tenantUserDetailsHref,
  tenantInvoiceDetailsHref,
  currentTenantId,
  isSuperAdmin,
  handlePayNowButton,
}: Props) {
  const menuActions = usePopover();
  const [selectedId, setSelectedId] = useState<any>('');
  const openinvoiceUtils = (event: React.MouseEvent<HTMLElement>, row: any) => {
    menuActions.onOpen(event);
    setSelectedId(row);
  };
  const handleMenuClose = () => {
    setSelectedId('');
    menuActions.onClose();
  };

  const renderMenuActions = () => (
    <CustomPopover
      open={menuActions.open}
      anchorEl={menuActions.anchorEl}
      onClose={handleMenuClose}
      slotProps={{ arrow: { placement: 'right-top' } }}
    >
      <MenuList>
        <MenuItem
          component={RouterLink}
          href={
            isTenantInvoice
              ? paths.tenant.invoice(currentTenantId || '', selectedId?.id)
              : paths.setting.billing.details(selectedId?.id)
          }
          onClick={handleMenuClose}
        >
          <Iconify icon="solar:eye-bold" />
          {t('VIEW')}
        </MenuItem>
        {isTenantInvoice && selectedId && selectedId?.status !== 'paid' && (
          <MenuItem
            onClick={() => {
              handleMarkAsRead?.(selectedId?.id);
              handleMenuClose();
            }}
            disabled={selectedId?.status === 'paid'}
          >
            <Iconify icon="mdi:check-circle" />
            {t('MARK_TO_PAID')}
          </MenuItem>
        )}
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      {rows
        // .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
        .map(
          (
            row:
              | AccountList
              | TicketsList
              | IUserItem
              | TeamList
              | CustomLabelsList
              | ChannelData
              | IAccount
              | IContact
              | ITenant
              | ITicket,
            index
          ) => (
            <TableRow
              key={row.id ?? index + 1}
              selected={'id' in row ? table.selected.includes(row.id) : false}
              onClick={() => {
                if (isSelectRecordDialog) setRecordInputHandler?.(row);

                if ('id' in row && isSelectRecordDialog) {
                  table.onSelectRow(row.id, false);
                }
              }}
              sx={{ cursor: isSelectRecordDialog && !isSupportReport ? 'pointer' : 'default' }}
            >
              {selectRowPremission && (
                <TableCell padding="checkbox" key={`${index}-checkbox`}>
                  <Checkbox
                    checked={'id' in row ? table.selected.includes(row.id) : false}
                    onClick={() => 'id' in row && table.onSelectRow(row.id, !isCampaignTamplate)}
                    inputProps={{
                      id: 'id' in row ? `${row.id}-checkbox` : undefined,
                      'aria-label': 'id' in row ? `${row.id} checkbox` : undefined,
                    }}
                  />
                </TableCell>
              )}

              {headCells.map((headCell, headerIndex) => {
                const key = headCell.id;
                const value = (row as Record<string, any>)[key];
                const udatedValue =
                  value && value.length > 20 ? (
                    <Tooltip title={value}>
                      <Typography component="span" sx={{ fontSize: 'inherit' }}>
                        {truncateText(value, 20)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    value
                  );

                if (
                  [
                    'name',
                    'username',
                    'firstName',
                    'lastName',
                    'account_name',
                    'userName',
                    'teamName',
                    'title',
                    'email',
                    'ticketNo',
                    'id',
                  ].includes(key) &&
                  moduleName !== 'user'
                ) {
                  const firstChar =
                    'name' in row
                      ? row.name
                      : 'firstName' in row
                        ? row.firstName || row.lastName
                        : 'teamName' in row
                          ? row.teamName
                          : undefined;

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          {isSelectRecordDialog ||
                          isSupportReport ||
                          (moduleName === 'CAMPAIGN' &&
                            'campaignStatus' in row &&
                            row.campaignStatus !== 'completed') ? (
                            <Box key={key || headerIndex}>{udatedValue}</Box>
                          ) : (
                            <Link
                              component={RouterLink}
                              href={
                                'id' in row
                                  ? moduleName === 'bill' && isTenantInvoice
                                    ? tenantInvoiceDetailsHref
                                      ? tenantInvoiceDetailsHref(currentTenantId || '', row.id)
                                      : editHref(row.id)
                                    : editHref(row.id)
                                  : '#'
                              }
                              color="inherit"
                              sx={{ cursor: 'pointer' }}
                            >
                              {udatedValue}
                            </Link>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'profile' && moduleName !== 'user') {
                  const firstChar =
                    'name' in row
                      ? row.name
                      : 'firstName' in row
                        ? row.firstName || row.lastName
                        : 'teamName' in row
                          ? row.teamName
                          : undefined;

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Link
                          component={RouterLink}
                          href={
                            'id' in row
                              ? moduleName === 'bill' && isTenantInvoice
                                ? tenantInvoiceDetailsHref
                                  ? tenantInvoiceDetailsHref(currentTenantId || '', row.id)
                                  : editHref(row.id)
                                : editHref(row.id)
                              : '#'
                          }
                          color="inherit"
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'none', // 🔹 Removes underline on hover
                            },
                          }}
                        >
                          {'ProfileURL' in row && (
                            <Avatar
                              alt={firstChar}
                              src={'ProfileURL' in row ? row.ProfileURL || undefined : undefined}
                            >
                              {firstChar?.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                        </Link>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'contactId') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          {isSelectRecordDialog || isSupportReport ? (
                            <Box key={key || headerIndex}>{udatedValue}</Box>
                          ) : (
                            <Link
                              component={RouterLink}
                              href={
                                'contact' in row && row.contact?.id && contactDetailHref
                                  ? contactDetailHref(row.contact.id)
                                  : '#'
                              }
                              color="inherit"
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'none', // 🔹 Removes underline on hover
                                },
                              }}
                            >
                              {/* {moduleName === "call"
                                ? ('contact' in row && row.contact && (row.contact.firstName || row.contact.lastName)
                                  ? `${row.contact.firstName ?? ''} ${row.contact.lastName ?? ''}`.trim()
                                  : 'contact' in row
                                    ? ('email' in row
                                      ? row.contact?.email
                                      : row.contact?.phone
                                    )
                                    : ''
                                )
                   : udatedValue} */}
                              {udatedValue}
                            </Link>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }
                if (key === 'accountName') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          {isSelectRecordDialog || isSupportReport ? (
                            <Box key={key || headerIndex}>{udatedValue}</Box>
                          ) : (
                            <Link
                              component={RouterLink}
                              href={
                                ('companyId' in row ||
                                  ('contact' in row && row.contact?.company?.id)) &&
                                accountDetailHref
                                  ? accountDetailHref(
                                      'contact' in row ? row.contact?.company?.id : row.companyId
                                    )
                                  : '#'
                              }
                              color="inherit"
                              sx={{ cursor: 'pointer' }}
                            >
                              {/* {moduleName === "call"
                                ? ('contact' in row
                                  ? row.contact?.company?.name || ''
                                  : ''
                                )
                                : udatedValue} */}
                              {udatedValue}
                            </Link>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'assignUserListName') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          {isSelectRecordDialog || isSupportReport ? (
                            <Box key={key || headerIndex}>{udatedValue}</Box>
                          ) : (
                            <Link
                              component={RouterLink}
                              href={
                                'assignedType' in row &&
                                row.assignedType &&
                                userDetailHref &&
                                teamsDetailHref
                                  ? row.assignedType === 'USER'
                                    ? userDetailHref(row.assignedId)
                                    : teamsDetailHref(row.assignedId)
                                  : '#'
                              }
                              color="inherit"
                              sx={{ cursor: 'pointer' }}
                            >
                              {udatedValue}
                            </Link>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }

                if (
                  [
                    'name',
                    'username',
                    'firstName',
                    'account_name',
                    'userName',
                    'teamName',
                    'email',
                  ].includes(key) &&
                  moduleName === 'user'
                ) {
                  const firstChar =
                    'name' in row
                      ? row.name
                      : 'firstName' in row
                        ? row.firstName || row.lastName
                        : 'teamName' in row
                          ? row.teamName
                          : undefined;

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        {/* {'ProfileURL' in row && key !== 'email' && (
                          <Avatar
                            alt={firstChar}
                            src={'ProfileURL' in row ? row.ProfileURL || undefined : undefined}
                          >
                            {firstChar?.charAt(0).toUpperCase()}
                          </Avatar>
                        )} */}

                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          <Link
                            component={RouterLink}
                            href={
                              'id' in row
                                ? isSuperAdmin
                                  ? tenantUserDetailsHref
                                    ? tenantUserDetailsHref((row as any)?.tenantId, row.id) // call it here
                                    : '#'
                                  : editHref(row.id)
                                : '#'
                            }
                            color="inherit"
                            sx={{ cursor: 'pointer' }}
                          >
                            {(row as any)?.firstName || (row as any)?.lastName
                              ? `${(row as any)?.firstName || ''} ${(row as any)?.lastName || ''}`
                              : 'email' in row
                                ? row.email
                                : ''}
                          </Link>
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            {(row as any)?.firstName || (row as any)?.lastName
                              ? 'email' in row
                                ? row.email
                                : ''
                              : ''}
                          </Box>
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }
                if (key === 'profile' && moduleName === 'user') {
                  const firstChar =
                    'name' in row
                      ? row.name
                      : 'firstName' in row
                        ? row.firstName || row.lastName
                        : 'teamName' in row
                          ? row.teamName
                          : undefined;

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Link
                          component={RouterLink}
                          href={
                            'id' in row
                              ? isSuperAdmin
                                ? tenantUserDetailsHref
                                  ? tenantUserDetailsHref((row as any)?.tenantId, row.id) // call it here
                                  : '#'
                                : editHref(row.id)
                              : '#'
                          }
                          color="inherit"
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'none', // 🔹 Removes underline on hover
                            },
                          }}
                        >
                          {'ProfileURL' in row && (
                            <Avatar
                              alt={firstChar}
                              src={'ProfileURL' in row ? row.ProfileURL || undefined : undefined}
                            >
                              {firstChar?.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                        </Link>
                      </Box>
                    </TableCell>
                  );
                }

                if (
                  key === 'createdAt' ||
                  key === 'updatedAt' ||
                  key === 'callDateTs' ||
                  key === 'createdDate' ||
                  key === 'due_date' ||
                  key === 'date' ||
                  key === 'issuedDate' ||
                  key === 'lastModified' ||
                  key === 'createdTime' ||
                  key === 'lastActivity' ||
                  key === 'dueDate'
                ) {
                  const timeStempfields = ['callDateTs', 'date', 'due_date'];
                  const updatedValue = timeStempfields.includes(key) ? value * 1000 : value;

                  const formattedDate = convertUTCToLocal(updatedValue);

                  return (
                    <TableCell key={key || headerIndex}>
                      <Typography variant="body2"> {formattedDate?.date}</Typography>
                      <Typography variant="body2" sx={{ ml: 3, fontSize: '0.75rem' }}>
                        {formattedDate.time}
                      </Typography>
                    </TableCell>
                  );
                }

                if (key === 'channelProfile') {
                  const selectedProfileType = userType.find(
                    (profile) => 'channelType' in row && profile.type === row.channelType
                  );

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          {isSelectRecordDialog || isSupportReport ? (
                            <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                              <Image
                                src={selectedProfileType?.icon}
                                alt={selectedProfileType?.icon}
                                sx={{
                                  width: 25,
                                  height: 25,
                                  mr: 1,
                                }}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                              <Image
                                src={selectedProfileType?.icon}
                                alt={selectedProfileType?.icon}
                                sx={{
                                  width: 25,
                                  height: 25,
                                  mr: 1,
                                }}
                              />
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }
                if (key === 'channelName') {
                  const selectedProfileType = userType.find(
                    (profile) => 'channelType' in row && profile.type === row.channelType
                  );

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          {isSelectRecordDialog || isSupportReport ? (
                            <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2"> {value}</Typography>
                            </Box>
                          ) : (
                            <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2"> {value}</Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'source') {
                  const contactId = (row as any)?.parties?.from?.contactId;
                  return (
                    <TableCell key={key}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          <Link
                            component={RouterLink}
                            href={
                              contactId && contactDetailHref ? contactDetailHref(contactId) : '#'
                            }
                            color="inherit"
                            underline={contactId ? 'hover' : 'none'}
                            sx={{ cursor: contactId ? 'pointer' : 'default' }}
                          >
                            <Typography variant="body2">
                              {' '}
                              {(row as any)?.parties?.from?.display ?? ''}
                            </Typography>
                            {!(row as any)?.parties?.from?.numberView && (
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {(row as any)?.parties?.from?.number ?? ''}
                              </Typography>
                            )}
                          </Link>
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'destination') {
                  const contactId = (row as any)?.parties?.to?.contactId;
                  return (
                    <TableCell key={key}>
                      <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
                        <Stack
                          sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}
                        >
                          <Link
                            component={RouterLink}
                            href={
                              contactId && contactDetailHref ? contactDetailHref(contactId) : '#'
                            }
                            color="inherit"
                            underline={contactId ? 'hover' : 'none'}
                            sx={{ cursor: contactId ? 'pointer' : 'default' }}
                          >
                            <Typography variant="body2">
                              {' '}
                              {(row as any)?.parties?.to?.display ?? ''}
                            </Typography>
                            {!(row as any)?.parties?.to?.numberView && (
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {(row as any)?.parties?.to?.number ?? ''}
                              </Typography>
                            )}
                          </Link>
                        </Stack>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'url') {
                  const updateEventLabels =
                    'events' in row && Array.isArray(row.events)
                      ? row.events.map((eventKey: string) => {
                          const match = WEBHOOK_EVENTS.find((item) => item.key === eventKey);
                          return match?.label || eventKey;
                        })
                      : [];

                  const joinedLabels = updateEventLabels.join(', ');
                  const url = 'url' in row && row.url;
                  const updatedValue = joinedLabels && (
                    // <Tooltip title={joinedLabels}>
                    <>
                      <Typography color="text.primary">
                        {typeof url === 'string' ? url : ''}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', fontSize: 'inherit' }}
                      >
                        <strong>{t('SUBSCRIBE_EVENTS')}:</strong> {joinedLabels}
                      </Typography>
                    </>
                  );

                  return <TableCell key={key || headerIndex}>{updatedValue}</TableCell>;
                }

                if (key === 'action') {
                  const isDisabled =
                    'campaignStatus' in row && row.campaignStatus
                      ? ['completed', 'running'].includes(String(row?.campaignStatus))
                      : false;
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                          ...('channelType' in row && {
                            justifyContent: 'flex-end',
                          }),
                          // justifyContent: 'center',
                        }}
                      >
                        {handleCustomiseDID &&
                          handleIVRConnection &&
                          isEdit &&
                          'channelType' in row &&
                          row.channelType === 'VOICE' && (
                            <>
                              <Tooltip title={t('IVR_CONFIGURATION')}>
                                <IconButton
                                  onClick={() => 'id' in row && handleIVRConnection(row.id)}
                                >
                                  <Iconify
                                    icon="majesticons-phone-dial"
                                    sx={{ color: 'var(--palette-action-active)' }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('CUSTOMIZE_DID')}>
                                <IconButton
                                  onClick={() => 'id' in row && handleCustomiseDID(row.id)}
                                >
                                  <Iconify
                                    icon="solar-call-medicine-rounded-bold"
                                    sx={{ color: 'var(--palette-action-active)' }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        {/* EDIT BTN */}
                        {handleEditContact &&
                          isEdit &&
                          (isDisabled ? (
                            <IconButton disabled>
                              {'Settingname' in row ? (
                                <Iconify icon="solar-settings-bold-duotone" />
                              ) : (
                                <Iconify icon="solar:pen-bold" />
                              )}
                            </IconButton>
                          ) : (
                            <Tooltip title={t('EDIT')}>
                              <IconButton
                                onClick={() =>
                                  'id' in row && handleEditContact && handleEditContact(row.id)
                                }
                              >
                                {'Settingname' in row ? (
                                  <Iconify icon="solar-settings-bold-duotone" />
                                ) : (
                                  <Iconify icon="solar:pen-bold" />
                                )}
                              </IconButton>
                            </Tooltip>
                          ))}

                        {/* DELETE BTN */}
                        {isDelete && (
                          <Tooltip title={t('DELETE')}>
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => 'id' in row && handleDeleteRecord?.(row.id)}
                              >
                                <Iconify
                                  icon="solar:trash-bin-trash-bold"
                                  sx={{ color: 'var(--palette-error-main) !important' }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                }
                if (key === 'playRecording') {
                  const isPlaying = currentlyPlayingId === row.id;

                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {'recordingUrl' in row && typeof row.recordingUrl === 'string' && (
                          <Tooltip
                            title={
                              row.recordingUrl
                                ? isPlaying
                                  ? t('STOP_RECORDING')
                                  : t('PLAY_RECORDING')
                                : ''
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => playCallRecording?.(row.id)}
                                sx={{
                                  animation: isPlaying ? 'pulse 1s infinite ease-in-out' : 'none',
                                }}
                                disabled={!row.recordingUrl}
                              >
                                <Iconify
                                  icon={
                                    isPlaying ? 'solar:stop-circle-bold' : 'solar:play-circle-bold'
                                  }
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                }
                if (key === 'phone') {
                  // if (key === 'phone' || key === 'destination') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Link
                        component="button"
                        onClick={() => handlePhoneNumberClick?.(row)}
                        color="inherit"
                        sx={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                      >
                        {formatPhoneNumber(value)}
                      </Link>
                    </TableCell>
                  );
                }

                if (key === 'clid') {
                  return (
                    <TableCell key={key || headerIndex}>
                      {'callerType' in row && row.callerType === 'CONTACT' ? (
                        <Link
                          component={RouterLink}
                          href={
                            'contact' in row && row.contact?.id && contactDetailHref
                              ? contactDetailHref(row.contact.id)
                              : '#'
                          }
                          color="inherit"
                          sx={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                        >
                          {value}
                        </Link>
                      ) : (
                        <Tooltip title={value || ''}>
                          <Typography variant="body2">{value}</Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  );
                }

                if ('status' in row || 'labelType' in row) {
                  if (['status', 'labelType'].includes(key)) {
                    const setColor = () => {
                      if (
                        'status' in row &&
                        row.status &&
                        !['conversation', 'inbox', 'campaign'].includes(moduleName ?? '')
                      ) {
                        const rowStatus = row.status.toLocaleLowerCase();
                        if (
                          ['active', 'completed', 'approved', 'answered', 'paid'].includes(
                            rowStatus
                          )
                        )
                          return 'success';
                        if (['pending', 'in progress'].includes(rowStatus)) return 'warning';
                        if (
                          [
                            'banned',
                            'inactive',
                            'rejected',
                            'failed',
                            'open',
                            'payment_due',
                          ].includes(rowStatus)
                        )
                          return 'error';
                        if (['on hold'].includes(rowStatus)) return 'info';
                        return 'default';
                      }
                      if (
                        'status' in row &&
                        row.status &&
                        ['conversation', 'inbox'].includes(moduleName ?? '') &&
                        !['campaign'].includes(moduleName ?? '')
                      ) {
                        const rowStatus = row.status.toLocaleLowerCase();
                        if (['active', 'completed', 'approved', 'open'].includes(rowStatus))
                          return 'success';
                        if (['pending', 'in progress'].includes(rowStatus)) return 'warning';
                        if (
                          ['banned', 'inactive', 'rejected', 'failed', 'close'].includes(rowStatus)
                        )
                          return 'error';
                        if (['on hold'].includes(rowStatus)) return 'info';
                        return 'default';
                      }

                      if (
                        'status' in row &&
                        row.status &&
                        !['conversation', 'inbox'].includes(moduleName ?? '') &&
                        ['campaign'].includes(moduleName ?? '')
                      ) {
                        const rowStatus = row.status.toLocaleLowerCase();
                        if (['read'].includes(rowStatus)) return 'success';
                        if (['delivered'].includes(rowStatus)) return 'warning';
                        if (['failed'].includes(rowStatus)) return 'error';
                        if (['sent'].includes(rowStatus)) return 'info';
                        return 'default';
                      }

                      if ('labelType' in row && row.labelType) {
                        const extraTypes = [
                          'Campaign',
                          'Project',
                          'Account',
                          'Recruitment',
                          'Management',
                          'Collaboration',
                        ];
                        if (row.labelType === 'Contact') return 'success';
                        if (row.labelType === 'Ticket') return 'warning';
                        if (row.labelType === 'Conversation') return 'error';
                        if (extraTypes.includes(row.labelType)) return 'error';
                        // Need to add more label types here
                        return 'default';
                      }

                      return 'default';
                    };

                    function getLabelByValue(labelvalue: string): string {
                      const tab = baseTabs.find((tab1: any) => tab1.value === labelvalue);
                      return tab ? tab.label : labelvalue || 'UNKNOWN';
                    }
                    return (
                      <TableCell key={key || headerIndex}>
                        <Label variant="soft" color={setColor()} sx={{ cursor: 'pointer' }}>
                          {'status' in row ? (
                            moduleName === 'call' ? (
                              row?.status
                            ) : moduleName === 'campaign' ? (
                              <Tooltip
                                title={
                                  row?.status?.toUpperCase() === 'FAILED'
                                    ? String('errorReason' in row && row.errorReason)
                                    : ''
                                }
                              >
                                <span>{t(getLabelByValue(row?.status || ''))}</span>
                              </Tooltip>
                            ) : (
                              t(row?.status?.toUpperCase().replace(/ /g, '_'))
                            )
                          ) : (
                            t(row?.labelType?.toUpperCase().replace(/ /g, '_'))
                          )}
                        </Label>
                      </TableCell>
                    );
                  }
                }

                if (key === 'priority' && 'priority' in row) {
                  const priorityColorMap: { [key: string]: string } = {
                    normal: 'success',
                    low: 'warning',
                    high: 'error',
                    banned: 'error',
                    inactive: 'error',
                  };

                  return (
                    <TableCell key={key || headerIndex}>
                      <Label
                        variant="soft"
                        color={
                          (priorityColorMap[row?.priority?.toLowerCase()] as LabelColor) ||
                          'default'
                        }
                      >
                        {t(row?.priority?.toUpperCase())}
                      </Label>
                    </TableCell>
                  );
                }

                if (key === 'bill_status' && 'bill_status' in row) {
                  const priorityColorMap: { [key: string]: string } = {
                    active: 'success',
                    inactive: 'error',
                  };

                  return (
                    <TableCell key={key || headerIndex}>
                      <Label
                        variant="soft"
                        color={(priorityColorMap[value?.toLowerCase()] as LabelColor) || 'default'}
                      >
                        {t(value?.toUpperCase())}
                      </Label>
                    </TableCell>
                  );
                }

                // Displaying the Custom Label color in circular shape
                if (key === 'labelColor' && 'labelColor' in row) {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box
                        sx={{
                          height: 18,
                          width: 18,
                          borderRadius: '50%',
                          background: row.labelColor,
                        }}
                      />
                    </TableCell>
                  );
                }

                if (key === 'labels') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {'labels' in row &&
                          row.labels?.map((label, idx) => (
                            <Label
                              key={idx}
                              variant="soft"
                              // color={label.labelColor as any}
                              sx={{
                                background: label.labelColor,
                                color: getContrastYIQ(label.labelColor),
                              }}
                            >
                              {label.labelName}
                            </Label>
                          ))}
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'resendMail' && isEdit) {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                          // justifyContent: 'center',
                        }}
                      >
                        {/* EDIT BTN */}
                        <Tooltip title={t('EDIT')}>
                          <span>
                            <IconButton
                              onClick={() =>
                                'id' in row && handleEditContact && handleEditContact(row.id)
                              }
                            >
                              {'Settingname' in row ? (
                                <Iconify icon="solar-settings-bold-duotone" />
                              ) : (
                                <Iconify icon="solar:pen-bold" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title={t('RESEND_MAIL')}>
                          <span>
                            <IconButton
                              disabled={Boolean('isEmailVerified' in row && row.isEmailVerified)}
                              color="primary"
                              onClick={() => 'id' in row && handleResendMail?.(row.id)}
                            >
                              <Iconify
                                icon="mdi:email-resend"
                                sx={{
                                  color:
                                    'isEmailVerified' in row && row.isEmailVerified
                                      ? 'text.disabled'
                                      : 'var(--palette-primary-main)',
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {moduleName === 'user' && userRole === 'Super Admin' && (
                          <Tooltip title={t('AUTO_LOGIN')}>
                            <span>
                              <IconButton onClick={() => 'id' in row && handleLoginAsUser?.(row)}>
                                <Iconify icon="solar-copy-bold" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'direction') {
                  // return <TableCell>{t(value?.toUpperCase()?.replace(/ /g, '_'))}</TableCell>;
                  return <TableCell key={key || headerIndex}>{value}</TableCell>;
                }
                if (key === 'amount_due' || key === 'total') {
                  // return <TableCell>{t(value?.toUpperCase()?.replace(/ /g, '_'))}</TableCell>;
                  return <TableCell key={key || headerIndex}>{formatPrice(value)}</TableCell>;
                }

                if (key === 'tamplateName') {
                  return <TableCell key={key || headerIndex}>{value}</TableCell>;
                }
                if (key === 'role') {
                  return (
                    <TableCell key={key || headerIndex}>{(row as any)?.Role?.name ?? ''}</TableCell>
                  );
                }
                if (key === 'reportToUser') {
                  return (
                    <TableCell key={key || headerIndex}>
                      {(row as any)?.reportToUser?.firstName || (row as any)?.reportToUser?.lastName
                        ? `${(row as any)?.reportToUser?.firstName || ''} ${(row as any)?.reportToUser?.lastName || ''}`
                        : (row as any)?.reportToUser?.email || ''}
                    </TableCell>
                  );
                }

                if (key === 'previewAction') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                          // justifyContent: 'center',
                        }}
                      >
                        <Tooltip title="Preview">
                          <IconButton
                            color="primary"
                            onClick={() => 'id' in row && handlePreview?.(row.id)}
                          >
                            <Iconify icon="mdi:eye" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'description') {
                  const parsedString = htmlToText(value);

                  return (
                    <TableCell key={key || headerIndex}>
                      <Tooltip title={parsedString}>
                        <Typography variant="body2">{truncateText(parsedString, 20)}</Typography>
                      </Tooltip>
                    </TableCell>
                  );
                }
                if (key === 'bill_name') {
                  return (
                    <TableCell key={key || headerIndex}>
                      {value?.length > 50 ? (
                        <Tooltip title={value}>
                          <Typography variant="body2">{truncateText(value, 50)}</Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">{value}</Typography>
                      )}
                    </TableCell>
                  );
                }

                if (key === 'addonName') {
                  return (
                    <TableCell key={key || headerIndex}>
                      <Tooltip title={value}>
                        <Typography variant="body2">{truncateText(value, 50)}</Typography>
                      </Tooltip>
                    </TableCell>
                  );
                }

                if (key === 'channelType') {
                  const cleanedValue = value.replace(/_/g, ' '); // replaces underscores with spaces
                  return (
                    <TableCell key={key || headerIndex}>
                      <Tooltip title={cleanedValue}>
                        <Typography variant="body2">{truncateText(cleanedValue, 50)}</Typography>
                      </Tooltip>
                    </TableCell>
                  );
                }

                if (key === 'invoiceUtils') {
                  return (
                    <TableCell align="right" sx={{ px: 1 }} key={key || headerIndex}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isTenantInvoice && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handlePayNowButton?.(row)}
                            disabled
                            // disabled={'status' in row && row.status === 'paid'}
                          >
                            {t('PAY_NOW')}
                          </Button>
                        )}
                        <IconButton
                          color={menuActions.open ? 'inherit' : 'default'}
                          onClick={(e) => openinvoiceUtils(e, row)}
                        >
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  );
                }

                if (key === 'redirect') {
                  return (
                    <TableCell
                      key={key || headerIndex}
                      sx={{
                        p: 1,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        width: 60, // Fixed width for consistency
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          height: '100%',
                          minHeight: 40, // Ensure minimum height for proper centering
                        }}
                      >
                        <Tooltip title={t('REDIRECT_INBOX')}>
                          <IconButton
                            onClick={() => handleOpenConversation?.(row)}
                            sx={{
                              p: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <Iconify icon="tabler-message-forward" width={24} height={24} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  );
                }

                return <TableCell key={key || headerIndex}>{udatedValue}</TableCell>;
              })}
            </TableRow>
          )
        )}

      {renderMenuActions()}
    </>
  );
}
